import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { REST_API_URL } from './rest-api.token';
import { firstValueFrom } from 'rxjs';

export class BaseModel {
  public FechaAlta?: Date;
  public FechaModificacion?: Date;
  public FechaBaja?: Date;

  public BaseModel() {}
}

export class AplicacionModel extends BaseModel {
  public Id: number;
  public Nombre: string;
  public Imagen: string;
}

export class RolModel extends BaseModel {
  public Id?: number;
  public Nombre: string;
  public Descripcion: string;
}

export class PermisoModel extends BaseModel {
  public Id: number;
  public Nombre: string;
  public Descripcion: string;
  public Acronimo: string;
}

export class MenuModel {
  Id: number;
  Nombre: string;
  Observaciones: string;
  Opciones: MenuOptionsModel[];
  FechaAlta: Date;
  FechaModificacion: Date;
  FechaBaja: Date;
}

export class MenuOptionsModel {
  Id: number;
  MenuId: number;
  PadreId?: number;
  Orden: number;
  TextoId: string;
  AccionId: string;
  SoloMovisat: boolean;
  Observaciones?: string;
  OpcionOriginalId?: number;

  // Agregados
  Level: number;
}

@Injectable({
  providedIn: 'root',
})
export class RolesPermissionsService {
  constructor(
    private readonly http: HttpClient,
    @Optional() @Inject(REST_API_URL) private apiUrl?: string,
  ) {}

  getOpctionsWithPermisionsByMenu(menuId: number, appId: number): Promise<any> {
    if (!this.apiUrl) {
      throw new Error('REST_API_URL is not provided!');
    }
    try {
      return firstValueFrom(
        this.http.get<any>(
          `${this.apiUrl}/sso/menu/permisos/opciones/lista?aplicacionId=${appId}&menuId=${menuId}`,
        ),
      );
    } catch (e) {
      console.log(e);
    }
  }

  async exportPermisosExcel(
    idAplicacion: number,
    idMenu: number,
    idPermiso: number,
  ): Promise<HttpResponse<Blob> | null> {
    if (!this.apiUrl) {
      throw new Error('REST_API_URL is not provided!');
    }
    try {
      const url = `${this.apiUrl}/sso/permisos/exportar/excel/?idAplicacion=${idAplicacion}&idPermiso=${idPermiso}&idMenu=${idMenu}`;
      // use observe response to get headers (filename)
      return (await firstValueFrom(
        this.http.get(url, {
          observe: 'response',
          responseType: 'blob' as 'json',
        }),
      )) as unknown as HttpResponse<Blob>;
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  async getApps(): Promise<AplicacionModel[]> {
    let result: AplicacionModel[] = null;

    try {
      result = await firstValueFrom(
        this.http.get<AplicacionModel[]>(`${this.apiUrl}/sso/aplicaciones`),
      );
    } catch (e) {
      console.log(e);
      window.alert(e.error.error);
    }
    return result;
  }

  getMenuByApplication(appId: number): Promise<any> {
    try {
      return firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/sso/menus?aplicacion=${appId}`),
      );
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  async getRolesApp(app: number): Promise<RolModel[]> {
    let result: RolModel[];
    try {
      result = await firstValueFrom(
        this.http.get<RolModel[]>(`${this.apiUrl}/sso/aplicacion/roles/${app}`),
      );
    } catch (e) {
      console.log(e);
      window.alert(e.error.error);
    }
    return result ? result : [];
  }

  async getPermisos(): Promise<PermisoModel[]> {
    try {
      return await firstValueFrom(
        this.http.get<PermisoModel[]>(`${this.apiUrl}/sso/usuarios/permisos`),
      );
    } catch (e) {
      console.log(e);
    }
    return [];
  }

  getMenuPermissionsByOption(
    appId: number,
    menuId: number,
    menuOpcionId: number,
  ): Promise<any> {
    try {
      return firstValueFrom(
        this.http.get<any>(
          `${this.apiUrl}/sso/menu/permisos?aplicacionId=${appId}&menuId=${menuId}&menuOpcionId=${menuOpcionId}`,
        ),
      );
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  saveMenuPermissionsByOptions(payload: {
    appId: number;
    menuId: number;
    menuOpcionIds: number[];
    permisos: { IdRol: number; IdPermiso: number }[];
  }): Promise<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    const payloadToSend = {
      aplicacionId: payload.appId,

      menuId: payload.menuId,

      idMenuOpcion: payload.menuOpcionIds,
      permisos: payload.permisos,
    };
    try {
      return firstValueFrom(
        this.http.post<any>(
          `${this.apiUrl}/sso/menu/permisos`,
          JSON.stringify(payloadToSend),
          httpOptions,
        ),
      );
    } catch (e) {
      console.log(e);
    }
  }

  // Obtener el menú de una aplicación con todas las opciones de los módulos que se hayan definido para esta app
  async getMenuAppCompleto(menuId: number): Promise<MenuModel> {
    let result: MenuModel;
    try {
      result = await firstValueFrom(
        this.http.get<MenuModel>(`${this.apiUrl}/sso/menu/completo/${menuId}`),
      );
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  getMenus() {
    try {
      return firstValueFrom(this.http.get<any>(`${this.apiUrl}/sso/menus`));
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  getMenusAplicacionesEmpresas() {
    const promiseAll = [];
    try {
      promiseAll.push(
        firstValueFrom(this.http.get<any>(`${this.apiUrl}/sso/menus`)),
      );
      promiseAll.push(
        firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/sso/menus/aplicaciones`),
        ),
      );
      promiseAll.push(
        firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/sso/menus/aplicaciones/empresas`),
        ),
      );
      return Promise.all(promiseAll);
    } catch (e) {
      console.log(e);
    }
    return null;
  }
}
